import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import logo from "../../../assets/images/logo.png";
import "./Invoice.css";

function Invoice() {
  const navigate = useNavigate();
  const invoiceRef = useRef(null);

  const lastOrder = JSON.parse(localStorage.getItem("lastOrder")) || null;

  const shippingData =
    lastOrder?.shippingData ||
    JSON.parse(localStorage.getItem("shippingData")) ||
    {};

  const cartItems =
    lastOrder?.cart ||
    JSON.parse(localStorage.getItem("cart")) ||
    [];

  const subtotal = Number(
    lastOrder?.subtotal ??
    cartItems.reduce(
      (sum, item) => sum + Number(item.price) * Number(item.qty),
      0
    )
  );

  const shipping = Number(
    lastOrder?.shipping ??
    lastOrder?.shipping_charge ??
    0
  );

  const isCryptoOrder = (lastOrder?.paymentMethod || "")
    .toLowerCase()
    .includes("crypto");

  const discount = isCryptoOrder ? Number(lastOrder?.discount || 0) : 0;

  const total = Number(
    lastOrder?.total ??
    (subtotal + shipping - discount)
  );

  const invoiceNumber =
    lastOrder?.invoiceNumber ||
    `ORD-${Date.now()}`;

  const invoiceDate = lastOrder?.orderDate
    ? new Date(lastOrder.orderDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/my-orders");
    }
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    let y = 50;

    const BRAND_BLUE = [46, 42, 140]; // #2e2a8c

    const drawBorder = () => {
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(1.2);
      doc.rect(15, 15, pageWidth - 30, pageHeight - 30);
    };

    drawBorder();

    // ===== HEADER =====
    try {
      doc.addImage(logo, "PNG", margin, y - 22, 110, 32);
    } catch (e) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      doc.setTextColor(...BRAND_BLUE);
      doc.text("Kamagrastar", margin, y);
    }

    doc.setFontSize(11);
    doc.setTextColor(90, 90, 90);
    doc.setFont("helvetica", "normal");
    doc.text("INVOICE", pageWidth - margin, y, { align: "right" });

    y += 18;
    doc.setFontSize(10);
    doc.text(`Reference #: ${invoiceNumber}`, pageWidth - margin, y, { align: "right" });

    y += 14;
    if (lastOrder?.orderId) {
      doc.text(`Order ID: #${lastOrder.orderId}`, pageWidth - margin, y, { align: "right" });
    }

    y += 14;
    doc.text(`Date: ${invoiceDate}`, pageWidth - margin, y, { align: "right" });

    y += 30;
    doc.setDrawColor(225, 225, 225);
    doc.setLineWidth(1);
    doc.line(margin, y, pageWidth - margin, y);

    // ===== BILL TO =====
    y += 25;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    doc.text("Bill To", margin, y);

    y += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(70, 70, 70);
    doc.text(`${shippingData.firstName || ""} ${shippingData.lastName || ""}`, margin, y);
    y += 14;
    doc.text(`${shippingData.address || ""}`, margin, y);
    y += 14;
    doc.text(`${shippingData.city || ""}, ${shippingData.country || ""}`, margin, y);
    y += 14;
    doc.text(`${shippingData.phone || ""}`, margin, y);

    // ===== TABLE HEADER =====
    y += 35;
    doc.setFillColor(238, 240, 250);
    doc.rect(margin, y, pageWidth - margin * 2, 24, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 30);
    doc.text("Item", margin + 10, y + 16);
    doc.text("Qty", pageWidth - margin - 160, y + 16, { align: "right" });
    doc.text("Price", pageWidth - margin - 90, y + 16, { align: "right" });
    doc.text("Total", pageWidth - margin - 10, y + 16, { align: "right" });

    y += 24;

    // ===== TABLE ROWS =====
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    cartItems.forEach((item) => {
      y += 24;
      if (y > 700) {
        doc.addPage();
        drawBorder();
        y = 50;
      }
      doc.setTextColor(40, 40, 40);
      doc.text(
        String(item.productName || item.product_name || item.name || "Item"),
        margin + 10,
        y
      );
      doc.text(String(Number(item.qty || 0)), pageWidth - margin - 160, y, { align: "right" });
      doc.text(`\u20AC${Number(item.price || 0).toFixed(2)}`, pageWidth - margin - 90, y, { align: "right" });
      doc.text(
        `\u20AC${(Number(item.price || 0) * Number(item.qty || 0)).toFixed(2)}`,
        pageWidth - margin - 10,
        y,
        { align: "right" }
      );
      doc.setDrawColor(240, 240, 240);
      doc.line(margin, y + 8, pageWidth - margin, y + 8);
    });

    // ===== TOTALS =====
    y += 35;
    doc.setFont("helvetica", "normal");
    doc.setTextColor(90, 90, 90);
    doc.text("Subtotal", pageWidth - margin - 160, y);
    doc.text(`\u20AC${subtotal.toFixed(2)}`, pageWidth - margin - 10, y, { align: "right" });

    y += 18;
    doc.text("Shipping", pageWidth - margin - 160, y);
    doc.text(`\u20AC${shipping.toFixed(2)}`, pageWidth - margin - 10, y, { align: "right" });

    if (discount > 0) {
      y += 18;
      doc.text("Discount (10%)", pageWidth - margin - 160, y);
      doc.text(`-\u20AC${discount.toFixed(2)}`, pageWidth - margin - 10, y, { align: "right" });
    }

    y += 10;
    doc.setDrawColor(225, 225, 225);
    doc.line(pageWidth - margin - 170, y, pageWidth - margin, y);

    y += 20;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...BRAND_BLUE);
    doc.text("Total", pageWidth - margin - 160, y);
    doc.text(`\u20AC${total.toFixed(2)}`, pageWidth - margin - 10, y, { align: "right" });

    // ===== FOOTER =====
    y += 50;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text("Thank you for shopping with Kamagrastar.", margin, y);
    y += 14;
    doc.text("For queries, contact support@kamagrastar.com", margin, y);

    doc.save(`${invoiceNumber}.pdf`);
  };

  return (
    <>
      <Helmet>
        <title>Invoice</title>
        <link rel="canonical" href="https://kamagrastar.eu/invoice" />
      </Helmet>

      <div className="invoice-page">

        <div className="invoice-container" ref={invoiceRef}>

          <div className="invoice-top">
            <div className="invoice-brand">
              <img src={logo} alt="Kamagrastar" className="invoice-logo" />
              <p>Online Pharmacy</p>
            </div>

            <div className="invoice-meta">
              <h3>INVOICE</h3>
              <p>Reference #: {invoiceNumber}</p>
              {lastOrder?.orderId && <p>Order ID: #{lastOrder.orderId}</p>}
              <p>Date: {invoiceDate}</p>
            </div>
          </div>

          <div className="invoice-divider"></div>

          <div className="invoice-bill-to">
            <h4>Bill To</h4>
            <p>{shippingData.firstName} {shippingData.lastName}</p>
            <p>{shippingData.address}</p>
            <p>{shippingData.city}, {shippingData.country}</p>
            <p>{shippingData.phone}</p>
          </div>

          <div className="invoice-table-wrapper">
            <table className="invoice-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {cartItems.length > 0 ? (
                  cartItems.map((item, index) => (
                    <tr key={index}>
                      <td>{item.productName || item.product_name || item.name}</td>
                      <td>{item.qty}</td>
                      <td>€{Number(item.price || 0).toFixed(2)}</td>
                      <td>€{(Number(item.price || 0) * Number(item.qty || 0)).toFixed(2)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="invoice-empty">No items found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="invoice-totals">
            <div className="invoice-total-row">
              <span>Subtotal</span>
              <span>€{subtotal.toFixed(2)}</span>
            </div>
            <div className="invoice-total-row">
              <span>Shipping</span>
              <span>€{shipping.toFixed(2)}</span>
            </div>
            {discount > 0 && (
              <div className="invoice-total-row">
                <span>Discount (10%)</span>
                <span>-€{discount.toFixed(2)}</span>
              </div>
            )}
            <div className="invoice-total-row grand">
              <span>Total</span>
              <span>€{total.toFixed(2)}</span>
            </div>
          </div>

          <div className="invoice-footer">
            <p>Thank you for shopping with Realgenericshop.</p>
            <p>For queries, contact support@realgenericshop.com</p>
          </div>

        </div>

        <div className="invoice-actions">
          <button className="invoice-back-btn" onClick={handleBack}>
            ← Back
          </button>
          <button className="invoice-download-btn" onClick={handleDownloadPDF}>
            Download PDF
          </button>
        </div>

      </div>
    </>
  );
}

export default Invoice;